import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { AnalysisTask } from '@/api/generated/model'
import { scoreTone } from '@/components/evals/analysis'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { filterSectionTasks, groupTaskSections, type SectionTask, type TaskSection } from './taskSections'

interface TaskSectionBlocksProps {
  tasks: AnalysisTask[]
  sections: ReadonlyMap<string, string>
  model: string | null
  taskPath: (slug: string) => string
}

function scoreLabel(score: number | null): string {
  return score === null ? 'Unscored' : `${(score * 100).toFixed(0)}%`
}

function TaskTile({ entry, to }: { entry: SectionTask; to: string }): React.JSX.Element {
  const label = `${entry.task.display_name}: ${scoreLabel(entry.score)}${entry.score === null ? '' : ' average'}`
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link to={to} aria-label={label} className={cn(
          'relative flex size-11 items-center justify-center border font-mono text-xs tabular-nums transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          entry.score === null ? 'border-dashed border-border bg-muted text-muted-foreground' : 'border-border text-foreground',
        )}>
          {entry.score !== null && <span aria-hidden className="absolute inset-0 opacity-25" style={{ backgroundColor: scoreTone(entry.score) }} />}
          <span className="relative" aria-hidden>{entry.score === null ? '—' : scoreLabel(entry.score)}</span>
        </Link>
      </TooltipTrigger>
      <TooltipContent><p className="max-w-xs">{label}</p></TooltipContent>
    </Tooltip>
  )
}

function SectionBlock({ section, taskPath }: { section: TaskSection; taskPath: (slug: string) => string }): React.JSX.Element {
  return (
    <section aria-label={section.name} className="space-y-3 border-t border-border pt-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-2"><h3 className="text-sm font-medium">{section.name}</h3><span className="text-xs text-muted-foreground">{section.tasks.length} {section.tasks.length === 1 ? 'task' : 'tasks'}</span></div>
        <p className="font-mono text-xs tabular-nums">{scoreLabel(section.score)}{section.score !== null && <span className="text-muted-foreground"> average · {section.scoredCount}/{section.tasks.length} scored</span>}</p>
      </header>
      <ul aria-label={`${section.name} tasks by average score`} className="flex flex-wrap gap-1.5">
        {section.tasks.map((entry) => <li key={entry.task.slug}><TaskTile entry={entry} to={taskPath(entry.task.slug)} /></li>)}
      </ul>
    </section>
  )
}

/** Usage: <TaskSectionBlocks tasks={analysisTasks} sections={sectionsBySlug} model={model} taskPath={taskPath} /> */
export function TaskSectionBlocks({ tasks, sections, model, taskPath }: TaskSectionBlocksProps): React.JSX.Element {
  const [query, setQuery] = useState('')
  const matching = filterSectionTasks(tasks, sections, query)
  const groups = groupTaskSections(matching, sections, model)
  return (
    <section aria-label="Tasks by section" className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="text-base font-semibold">Tasks by section</h2><p className="text-xs text-muted-foreground">Average score: highest first · Unscored last</p></div>
        <Input aria-label="Find a task or section" placeholder="Find a task or section" value={query} onChange={(event) => { setQuery(event.target.value) }} className="h-8 w-64" />
      </header>
      <TooltipProvider>{groups.map((section) => <SectionBlock key={section.name} section={section} taskPath={taskPath} />)}</TooltipProvider>
      {groups.length === 0 && <p className="text-sm text-muted-foreground">{tasks.length === 0 ? 'No tasks in this selection.' : 'No tasks match your search.'}</p>}
    </section>
  )
}
